/**
 * Does this element scroll further sideways than it shows?
 *
 * Measured rather than assumed, and that is the whole point. `base.css` turns the
 * sidebar into a one-line scrolling strip below 1024px, but *how much* of it fits is
 * a fact about the viewport and the person's role: nine destinations at 390px show
 * three, at 768px show eight, and at 900px show all nine. A media query can say
 * "this is the strip"; it cannot say "there is more of it than you can see", and a
 * cue that appeared at 900px would be telling a reader to scroll to something
 * already on screen. That is worse than no cue, because it is a lie they can check.
 *
 * `scrollWidth > clientWidth` is the question, asked at layout time and again
 * whenever the box changes size (`ResizeObserver`) or the window does. It is asked
 * once more whenever `watch` changes, because content can grow without the box
 * doing so — a client with three widgets gets three more nav items after the query
 * lands, and the element's own border box never moved.
 *
 * In jsdom every measurement is `0`, so this is `false` in every page test and the
 * cue does not render — which is right: there is no layout to overflow. The one
 * test that needs it true says so by defining the two properties.
 */

import { useLayoutEffect, useState, type RefObject } from 'react'

export function useOverflowsX(ref: RefObject<HTMLElement | null>, watch?: unknown): boolean {
  const [overflows, setOverflows] = useState(false)

  useLayoutEffect(() => {
    const element = ref.current
    if (element === null) return

    const measure = (): void => {
      setOverflows(element.scrollWidth > element.clientWidth)
    }
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(element)
    window.addEventListener('resize', measure)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [ref, watch])

  return overflows
}
